
import { getStorage, ref, uploadBytesResumable, deleteObject, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { app } from "./auth";
import { FIREBASE_CONFIG, MEDIA_BUCKET, FIREBASE_STORAGE_BASE_PATH } from '../constants';

let storage: any = null;

const initStorage = () => {
    if (storage) return storage;
    if (app) {
        try {
            storage = getStorage(app);
        } catch(e) {
            console.error("Storage init error", (e as any).message || e);
        }
    }
    return storage;
}

export const fileToBase64 = (file: File, maxWidth = 1024, quality = 0.7): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
            const elem = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            if (width > maxWidth) {
                height = height * (maxWidth / width);
                width = maxWidth;
            }
            elem.width = width;
            elem.height = height;
            const ctx = elem.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);
            resolve(elem.toDataURL(file.type === 'image/png' ? 'image/jpeg' : file.type, quality));
        };
        img.onerror = () => resolve("");
    };
    reader.onerror = () => resolve("");
  });
};

/**
 * Gets a tokenized download URL using the Firebase SDK.
 * Bypasses public URL construction when direct SDK access is needed.
 */
export const getSafeDownloadUrl = async (path: string): Promise<string> => {
    if (!path) return "";
    
    // Safety check for data URIs
    const trimmedPath = path.trim();
    if (trimmedPath.toLowerCase().startsWith('data:')) return trimmedPath;
    
    const s = initStorage();
    if (!s) return getResizedImageUrl(path, 'original');
    
    try {
        // If it's already a full URL with a token, just return it
        if (path.startsWith('http') && path.includes('token=')) return path;
        
        // Remove leading gs:// or bucket domain if present
        let cleanPath = path.replace(/^gs:\/\/[^/]+\//, '');
        if (cleanPath.startsWith('http')) {
            const parts = cleanPath.split(/\/o\//);
            if (parts.length > 1) {
                cleanPath = decodeURIComponent(parts[1].split('?')[0]);
            } else {
                // Extract path after bucket name for GCS URLs
                const bucketParts = cleanPath.split(/\.app\/|\.com\//);
                if (bucketParts.length > 1) {
                    cleanPath = bucketParts[1];
                }
            }
        }
        
        const storageRef = ref(s, cleanPath);
        return await getDownloadURL(storageRef);
    } catch (e) {
        console.warn("Failed to get tokenized URL, falling back to public domain:", (e as any).message || e);
        return getResizedImageUrl(path, 'original');
    }
}

export const uploadMedia = async (
    file: File, 
    path: string,
    onProgress?: (progress: number) => void
): Promise<string> => {
    const fallbackBase64 = await fileToBase64(file);
    const s = initStorage();
    if (!s) return fallbackBase64;
    
    const storageRef = ref(s, path);
    const auth = getAuth(app!);
    const currentUser = auth.currentUser;
    const metadata: any = { 
        contentType: file.type,
        customMetadata: {
            ownerEmail: currentUser?.email || 'anonymous'
        }
    };

    return new Promise((resolve) => {
        try {
            const uploadTask = uploadBytesResumable(storageRef, file, metadata);
            uploadTask.on('state_changed', 
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    if (onProgress) onProgress(progress);
                },
                (error) => {
                    console.warn("Upload failed, using fallback Base64:", error.message);
                    resolve(fallbackBase64); 
                },
                () => resolve(uploadTask.snapshot.ref.fullPath)
            );
        } catch (e) {
            resolve(fallbackBase64); 
        }
    });
};

export const deleteMedia = async (storagePath: string): Promise<boolean> => {
    const s = initStorage();
    if (!s || !storagePath || storagePath.startsWith('data:')) return false;
    
    try {
        const storageRef = ref(s, storagePath);
        await deleteObject(storageRef);
        return true;
    } catch (e) {
        console.error("Delete blocked by rules:", (e as any).message || e);
        return false;
    }
};

/**
 * Generates a high-performance GCS public URL for viewing.
 */
export const getResizedImageUrl = (
    storagePath: string | undefined | null,
    size: '300x300' | '1000x1000' | '1800x1800' | 'original',
    format: 'webp' | 'jpeg' = 'webp'
): string => {
    if (!storagePath) return '';
    
    // Robust detection of data URIs
    const trimmedPath = storagePath.trim();
    if (trimmedPath.toLowerCase().startsWith('data:')) return trimmedPath;

    const bucket = MEDIA_BUCKET;

    if (storagePath.startsWith('http')) {
        // If it's already a tokenized Firebase URL, just return it
        if (storagePath.includes('token=')) return storagePath;
        return storagePath;
    }

    // Firebase Storage internal paths
    const cleanPath = storagePath.replace(/^gs:\/\/[^/]+\//, '');
    const encodedPath = encodeURIComponent(cleanPath).replace(/%2F/g, '/');
    
    // Construct public URL
    return `https://storage.googleapis.com/${bucket}/${encodedPath}`;
};

/**
 * Updates the site's favicons and PWA manifest dynamically based on a provided image URL.
 */
export const updateDynamicFavicons = async (logoUrl: string) => {
    if (!logoUrl) return;

    const finalUrl = (logoUrl.startsWith('http') || logoUrl.startsWith('data:'))
        ? logoUrl
        : getResizedImageUrl(logoUrl, '300x300');

    // Update standard icon
    let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.href = finalUrl;

    // Update Apple touch icon
    let appleLink: HTMLLinkElement | null = document.querySelector("link[rel='apple-touch-icon']");
    if (!appleLink) {
        appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        document.head.appendChild(appleLink);
    }
    appleLink.href = finalUrl;
};